import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  where
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../contexts/AuthContext";

// Energia szerződés interface - Firebase kompatibilis
export interface EnergyContract {
  id: string;
  contractNumber: string;
  
  // Ügyfél kapcsolat
  customerId: string;
  customerName: string;
  customerType: 'individual' | 'organization';
  
  // POD kapcsolatok (több POD egy szerződéshez)
  pods: Array<{
    id: string;
    address: string;
    measurementType: string;
    annualConsumption: string;
  }>;
  
  // Szerződés alapadatok
  contractType: 'new' | 'modification' | 'renewal' | 'termination' | 'live';
  provider: 'EON' | 'ELMU' | 'DEMASZ' | 'OPUS' | 'MVMI';
  tariffType: 'residential' | 'commercial' | 'industrial';
  status: 'draft' | 'active' | 'pending' | 'expired' | 'terminated';
  
  // Időtartam
  startDate: string;
  endDate: string;
  
  // Árazás
  currency: string;
  pricePerKwh: number;
  monthlyFee: number;
  pricePackage: string;
  
  // Műszaki adatok
  capacity: number;
  profileNumber: string;
  
  // Metaadatok
  notes: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  
  // Kapcsolódó dokumentumok
  quotationId?: string;
  organizationId?: string;
}

export const useEnergyContracts = () => {
  const [contracts, setContracts] = useState<EnergyContract[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      const contractsRef = collection(db, 'energy_contracts');
      let q = query(contractsRef, orderBy('createdAt', 'desc'));

      // Szervezet alapú szűrés (ha van)
      if (user?.organizationId && user?.role !== "super_admin") {
        q = query(contractsRef, 
          where('organizationId', '==', user.organizationId), 
          orderBy('createdAt', 'desc')
        );
      }

      const snapshot = await getDocs(q);

      const loadedContracts = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          contractNumber: data.contractNumber,
          customerId: data.customerId,
          customerName: data.customerName,
          customerType: data.customerType,
          pods: data.pods || [],
          contractType: data.contractType,
          provider: data.provider,
          tariffType: data.tariffType,
          status: data.status,
          startDate: data.startDate,
          endDate: data.endDate,
          currency: data.currency,
          pricePerKwh: data.pricePerKwh,
          monthlyFee: data.monthlyFee,
          pricePackage: data.pricePackage,
          capacity: data.capacity,
          profileNumber: data.profileNumber,
          notes: data.notes,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          quotationId: data.quotationId,
          organizationId: data.organizationId
        } as EnergyContract;
      });

      setContracts(loadedContracts);
      setError(null);
    } catch (err) {
      console.error("Hiba az energia szerződések betöltése során:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  const addContract = useCallback(async (contractData: Omit<EnergyContract, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const contractsRef = collection(db, 'energy_contracts');

      // Filter out undefined values
      const filteredData = Object.entries(contractData).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);

      const newContract = {
        ...filteredData,
        organizationId: user?.organizationId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(contractsRef, newContract);

      // Frissítsük a helyi állapotot
      setContracts(prevContracts => [
        {
          ...contractData,
          id: docRef.id,
          organizationId: user?.organizationId,
          createdAt: new Date(),
          updatedAt: new Date()
        } as EnergyContract,
        ...prevContracts
      ]);

      return docRef.id;
    } catch (err) {
      console.error("Hiba az energia szerződés hozzáadása során:", err);
      throw err;
    }
  }, [user]);

  const updateContract = useCallback(async (id: string, contractData: Partial<Omit<EnergyContract, 'id' | 'createdAt' | 'updatedAt'>>) => {
    try {
      const contractRef = doc(db, 'energy_contracts', id);

      const updateData = {
        ...contractData,
        updatedAt: serverTimestamp()
      };

      await updateDoc(contractRef, updateData);

      // Frissítsük a helyi állapotot
      setContracts(prevContracts =>
        prevContracts.map(contract =>
          contract.id === id
            ? {
              ...contract,
              ...contractData,
              updatedAt: new Date()
            }
            : contract
        )
      );
    } catch (err) {
      console.error("Hiba az energia szerződés frissítése során:", err);
      throw err;
    }
  }, []);

  const deleteContract = useCallback(async (id: string) => {
    try {
      const contractRef = doc(db, 'energy_contracts', id);
      await deleteDoc(contractRef);

      // Frissítsük a helyi állapotot
      setContracts(prevContracts =>
        prevContracts.filter(contract => contract.id !== id)
      );
    } catch (err) {
      console.error("Hiba az energia szerződés törlése során:", err);
      throw err;
    }
  }, []);

  return {
    contracts,
    loading,
    error,
    refreshContracts: loadContracts,
    addContract,
    updateContract,
    deleteContract
  };
};